import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Sparkle, Check, Calculator } from "@phosphor-icons/react";
import { COUNTRIES, COUNTRY_BY_SLUG } from "../lib/countries";
import { generateItinerary } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { TripBoard } from "../components/TripBoard";
import { Seo, breadcrumbLd } from "../components/Seo";
import { ItineraryAssistant } from "../components/ItineraryAssistant";
import { track } from "../lib/analytics";
import { toast, Toaster } from "sonner";

const STYLES = [
  { key: "budget", label: "Budget" },
  { key: "balanced", label: "Balanced" },
  { key: "luxury", label: "Luxury" },
  { key: "adventure", label: "Adventure" },
];

const INTERESTS = [
  "Beaches",
  "Mountains",
  "Food",
  "Culture",
  "Nightlife",
  "Photography",
  "Wildlife",
  "Adventure sports",
  "Shopping",
  "Spa & wellness",
  "Temples & heritage",
  "Cafes",
];

const CITIES_BY_COUNTRY = {
  vietnam: ["Hanoi", "Ha Long Bay", "Sapa", "Hoi An", "Da Nang", "Ho Chi Minh City", "Phu Quoc", "Ninh Binh"],
  thailand: ["Bangkok", "Chiang Mai", "Phuket", "Krabi", "Koh Samui", "Pattaya", "Ayutthaya", "Koh Phi Phi"],
  bali: ["Ubud", "Seminyak", "Canggu", "Uluwatu", "Nusa Penida", "Kuta", "Sanur", "Gili Islands"],
  japan: ["Tokyo", "Kyoto", "Osaka", "Mount Fuji / Hakone", "Nara", "Hiroshima", "Sapporo", "Okinawa"],
  maldives: ["Male", "Maafushi", "Hulhumale", "North Male Atoll", "South Ari Atoll", "Baa Atoll"],
  oman: ["Muscat", "Nizwa", "Wahiba Sands", "Salalah", "Jebel Akhdar", "Sur", "Musandam"],
  malaysia: ["Kuala Lumpur", "Langkawi", "Penang", "Cameron Highlands", "Malacca", "Kota Kinabalu", "Borneo"],
  singapore: ["Marina Bay", "Sentosa", "Chinatown", "Little India", "Orchard", "Gardens by the Bay"],
  "sri-lanka": ["Colombo", "Kandy", "Ella", "Nuwara Eliya", "Galle", "Sigiriya", "Mirissa", "Yala"],
  nepal: ["Kathmandu", "Pokhara", "Chitwan", "Nagarkot", "Lumbini", "Everest Base Camp", "Annapurna"],
  philippines: ["Manila", "Palawan / El Nido", "Coron", "Cebu", "Bohol", "Siargao", "Boracay"],
  laos: ["Luang Prabang", "Vientiane", "Vang Vieng", "4000 Islands", "Plain of Jars"],
};

// Mirrors backend INDIAN_STATE_TO_IATA → maps state name to the nearest major Indian city
// for the itinerary "From" field. Keeps Budget→Itinerary pre-fill meaningful.
const INDIAN_STATE_TO_CITY = {
  "Andhra Pradesh": "Hyderabad",
  "Arunachal Pradesh": "Agartala",
  "Assam": "Guwahati",
  "Bihar": "Patna",
  "Chhattisgarh": "Raipur",
  "Delhi": "Delhi",
  "Goa": "Goa",
  "Gujarat": "Ahmedabad",
  "Haryana": "Delhi",
  "Himachal Pradesh": "Delhi",
  "Jammu & Kashmir": "Srinagar",
  "Jharkhand": "Ranchi",
  "Karnataka": "Bangalore",
  "Kerala": "Kochi",
  "Madhya Pradesh": "Indore",
  "Maharashtra": "Mumbai",
  "Manipur": "Imphal",
  "Meghalaya": "Guwahati",
  "Mizoram": "Aizawl",
  "Nagaland": "Dimapur",
  "Odisha": "Bhubaneswar",
  "Punjab": "Amritsar",
  "Rajasthan": "Jaipur",
  "Sikkim": "Bagdogra",
  "Tamil Nadu": "Chennai",
  "Telangana": "Hyderabad",
  "Tripura": "Agartala",
  "Uttar Pradesh": "Lucknow",
  "Uttarakhand": "Delhi",
  "West Bengal": "Kolkata",
  "Andaman & Nicobar": "Port Blair",
  "Chandigarh": "Chandigarh",
  "Puducherry": "Chennai",
};

// Returns YYYY-MM-DD for `today + offsetDays`.
const isoOffset = (offsetDays) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

export default function Itinerary() {
  const { country: slugParam } = useParams();
  const [searchParams] = useSearchParams();
  const [slug, setSlug] = useState(slugParam || "japan");
  const [origin, setOrigin] = useState("Delhi");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [travelers, setTravelers] = useState(2);
  const [style, setStyle] = useState("balanced");
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [foodPreference, setFoodPreference] = useState("both");
  const [transportPreference, setTransportPreference] = useState("mix");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [itin, setItin] = useState(null);
  // Budget value passed from /budget — surfaces as a soft banner above the form.
  const [budgetHint, setBudgetHint] = useState(null);

  const country = COUNTRY_BY_SLUG[slug];
  const cities = CITIES_BY_COUNTRY[slug] || [];

  // Pre-fill the form when arrived from the Budget Calculator:
  //   /itinerary/:slug?from=Delhi&days=7&travelers=2&style=balanced&budget=68400
  useEffect(() => {
    const from = searchParams.get("from");
    const daysParam = searchParams.get("days");
    const travelersParam = searchParams.get("travelers");
    const styleParam = searchParams.get("style");
    const budgetParam = searchParams.get("budget");
    const startParam = searchParams.get("start");
    const endParam = searchParams.get("end");

    let touched = false;
    if (from) {
      setOrigin(INDIAN_STATE_TO_CITY[from] || from);
      touched = true;
    }
    if (travelersParam) {
      const n = parseInt(travelersParam, 10);
      if (!Number.isNaN(n) && n > 0) setTravelers(n);
      touched = true;
    }
    if (styleParam && ["budget", "balanced", "luxury", "adventure"].includes(styleParam)) {
      setStyle(styleParam);
      touched = true;
    }
    // Explicit start/end win over days-derived dates (e.g. coming from Reality Check)
    if (startParam && /^\d{4}-\d{2}-\d{2}$/.test(startParam)) {
      setStart(startParam);
      touched = true;
    }
    if (endParam && /^\d{4}-\d{2}-\d{2}$/.test(endParam)) {
      setEnd(endParam);
      touched = true;
    }
    if (daysParam && !startParam) {
      const d = parseInt(daysParam, 10);
      if (!Number.isNaN(d) && d > 0) {
        // Suggest dates ~30 days out so the user can quickly start tweaking.
        setStart(isoOffset(30));
        setEnd(isoOffset(30 + d - 1));
        touched = true;
      }
    }
    if (budgetParam) {
      const b = parseInt(budgetParam, 10);
      if (!Number.isNaN(b) && b > 0) {
        setBudgetHint(b);
        touched = true;
      }
    }

    if (touched) {
      toast.success("Pre-filled from your budget plan", {
        description: "Tweak the dates / cities and hit generate.",
        duration: 5000,
      });
      track("itinerary_prefilled_from_budget", {
        country: slugParam || "japan",
        days: daysParam,
        style: styleParam,
      });
    }
    // We intentionally only run this once on mount with the initial query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (arr, setArr, v) => {
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  };

  const generate = async () => {
    if (!start || !end) {
      toast.error("Please pick start & end dates");
      return;
    }
    track("itinerary_generate_started", {
      country: slug,
      cities: selectedCities,
      style,
      interests: selectedInterests,
      travelers: Number(travelers),
    });
    setLoading(true);
    setItin(null);
    // Nudge the user down so they actually see the loader (and later the trip-board)
    // appear underneath the form. Especially helps on mobile where the form fills the
    // viewport and the loader/trip-board lands below the fold.
    toast("Crafting your trip-board…", {
      description: "Scroll down to watch it appear ↓",
      duration: 4000,
    });
    setTimeout(() => {
      const loader = document.querySelector('[data-testid="itinerary-loading"]');
      loader?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    try {
      const augmentedNotes = budgetHint
        ? `${notes ? notes + "\n\n" : ""}Target trip budget: ~₹${Number(budgetHint).toLocaleString("en-IN")} per person (from Flycher budget calculator). Keep stay/food/activity picks comfortably within this range.`
        : notes;
      const data = await generateItinerary({
        country_slug: slug,
        cities: selectedCities,
        start_date: start,
        end_date: end,
        travelers: Number(travelers),
        style,
        interests: selectedInterests,
        food_preference: foodPreference,
        transport_preference: transportPreference,
        notes: augmentedNotes,
        origin_city: origin,
      });
      setItin(data);
      track("itinerary_generated", {
        itinerary_id: data.id,
        country: slug,
        days: data.days?.length || 0,
        cities: selectedCities,
        style,
      });
      toast.success("Your itinerary is ready ✨", {
        description: "Scroll down to view your day-by-day trip-board ↓",
        duration: 5000,
      });
      setTimeout(() => {
        const el = document.getElementById("trip-board");
        el?.scrollIntoView({ behavior: "smooth" });
      }, 200);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      track("itinerary_generate_failed", {
        country: slug,
        error: detail || "unknown",
      });
      if (detail && /budget/i.test(detail)) {
        toast.error("AI key has reached its monthly budget", {
          description:
            "Top up via Profile → Universal Key → Add Balance, then try again. (This is a one-time fix; auto top-up keeps you covered.)",
          duration: 10000,
        });
      } else {
        toast.error(detail || "Failed to generate itinerary");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-mobile-nav" data-testid="itinerary-page">
      <Seo
        title={
          country
            ? `Free AI Itinerary Generator — ${country.name} day-by-day plan from India`
            : "Free AI Itinerary Generator & AI Trip Planner — Day-by-day plans from India"
        }
        description={
          country
            ? `Free AI itinerary generator for ${country.name} — generate a day-by-day AI trip plan in INR with cities, activities, food, and budget tips tuned for Indian travelers.`
            : "Free AI itinerary generator and AI trip planner for Indian travelers — day-by-day AI itineraries in INR, with budget hacks, downloadable PDF, and shareable trip-board."
        }
        path={slugParam ? `/itinerary/${slugParam}` : "/itinerary"}
        image={country?.image}
        jsonLd={breadcrumbLd(
          country
            ? [
                { name: "Home", path: "/" },
                { name: "Itinerary Builder", path: "/itinerary" },
                { name: country.name, path: `/itinerary/${country.slug}` },
              ]
            : [
                { name: "Home", path: "/" },
                { name: "Itinerary Builder", path: "/itinerary" },
              ],
        )}
      />
      <Toaster theme="dark" position="top-center" offset={80} />
      <Navbar />
      <div className="pt-24 max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="mb-6 sm:mb-8">
          <span className="kicker">— AI Itinerary Builder</span>
          <h1 className="font-serif text-3xl sm:text-6xl mt-3 max-w-3xl text-slate-900">
            A trip-board, drafted in{" "}
            <span className="italic" style={{ color: "#2A7FB8" }}>
              under a minute.
            </span>
          </h1>
          <p className="text-slate-600 mt-4 max-w-2xl text-sm leading-relaxed">
            Tell us the country and dates, pick a few cities (or let us decide),
            and we&apos;ll lay out every morning, afternoon and evening — with
            food, transfers and budgets baked in.
          </p>
        </div>

        {/* Budget pre-fill banner */}
        {budgetHint && (
          <div
            data-testid="budget-prefill-banner"
            className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-5 py-4 flex items-start gap-3"
          >
            <div className="w-9 h-9 rounded-xl grid place-items-center flex-shrink-0" style={{ background: "#3DB68B", color: "#fff" }}>
              <Calculator size={18} weight="bold" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-800">
                Building for a target budget of{" "}
                <strong style={{ color: "#3DB68B" }}>₹{Number(budgetHint).toLocaleString("en-IN")}</strong>{" "}
                per person. We'll lean into stays and activities that fit this range.
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5 font-mono uppercase tracking-wider">
                Pre-filled from your budget calculator
              </p>
            </div>
          </div>
        )}

        {/* Builder */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div
            data-testid="builder-form"
            className="lg:col-span-7 card-surface rounded-2xl p-6 space-y-6"
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Country">
                <select
                  data-testid="builder-country"
                  value={slug}
                  onChange={(e) => {
                    setSlug(e.target.value);
                    setSelectedCities([]);
                  }}
                  className="select-base"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.slug} value={c.slug}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="From">
                <input
                  data-testid="builder-origin"
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  className="input-base"
                />
              </Field>
              <Field label="Travelers">
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  data-testid="builder-travelers"
                  value={travelers}
                  onChange={(e) => setTravelers(e.target.value)}
                  className="input-base"
                />
              </Field>
              <Field label="Start date">
                <input
                  type="date"
                  data-testid="builder-start"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  className="input-base"
                />
              </Field>
              <Field label="End date">
                <input
                  type="date"
                  data-testid="builder-end"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  className="input-base"
                />
              </Field>
              <Field label="Style">
                <select
                  data-testid="builder-style"
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  className="select-base"
                >
                  {STYLES.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div>
              <div className="kicker mb-2">
                — Cities to cover ({selectedCities.length} selected)
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Not sure? Leave empty and we&apos;ll recommend the perfect mix.
              </p>
              <div className="flex flex-wrap gap-2">
                {cities.map((c) => {
                  const active = selectedCities.includes(c);
                  return (
                    <button
                      key={c}
                      data-testid={`city-chip-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      onClick={() =>
                        toggle(selectedCities, setSelectedCities, c)
                      }
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border transition ${
                        active
                          ? "text-white border-transparent shadow-sm"
                          : "border-slate-200 bg-white text-slate-700 hover:border-[#2A7FB8]/50"
                      }`}
                      style={active ? { background: "#2A7FB8" } : undefined}
                    >
                      {active && <Check size={12} weight="bold" />}
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="kicker mb-2">— Interests</div>
              <div className="flex flex-wrap gap-2">
                {INTERESTS.map((c) => {
                  const active = selectedInterests.includes(c);
                  return (
                    <button
                      key={c}
                      data-testid={`interest-${c.toLowerCase().replace(/\s/g, "-")}`}
                      onClick={() =>
                        toggle(selectedInterests, setSelectedInterests, c)
                      }
                      className={`px-3 py-1.5 rounded-full text-xs border transition ${
                        active
                          ? "border-[#2A7FB8]/60 text-[#2A7FB8] bg-[#EAF4FB]"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>

            <div data-testid="food-pref-section">
              <div className="kicker mb-2">— Food preference</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { v: "both", label: "Both", emoji: "🍱" },
                  { v: "veg", label: "Veg only", emoji: "🥗" },
                  { v: "non-veg", label: "Non-veg", emoji: "🍖" },
                  { v: "vegan", label: "Vegan", emoji: "🌱" },
                  { v: "jain", label: "Jain", emoji: "🪷" },
                  { v: "halal", label: "Halal", emoji: "☪️" },
                ].map((opt) => {
                  const active = foodPreference === opt.v;
                  return (
                    <button
                      key={opt.v}
                      data-testid={`food-pref-${opt.v}`}
                      onClick={() => setFoodPreference(opt.v)}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs border transition ${
                        active
                          ? "text-white border-transparent shadow-sm"
                          : "border-slate-200 bg-white text-slate-700 hover:border-[#2A7FB8]/50"
                      }`}
                      style={active ? { background: "#2A7FB8" } : undefined}
                    >
                      <span>{opt.emoji}</span> {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                We&apos;ll suggest 3 nearby eateries on every day that match your choice.
              </p>
            </div>

            <div data-testid="transport-pref-section">
              <div className="kicker mb-2">— Getting around</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { v: "mix", label: "Mix it up", emoji: "🧭" },
                  { v: "bike", label: "Rent a bike", emoji: "🛵" },
                  { v: "cab", label: "Cabs only", emoji: "🚕" },
                  { v: "transit", label: "Bus / Metro", emoji: "🚇" },
                ].map((opt) => {
                  const active = transportPreference === opt.v;
                  return (
                    <button
                      key={opt.v}
                      data-testid={`transport-pref-${opt.v}`}
                      onClick={() => setTransportPreference(opt.v)}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs border transition ${
                        active
                          ? "text-white border-transparent shadow-sm"
                          : "border-slate-200 bg-white text-slate-700 hover:border-[#2A7FB8]/50"
                      }`}
                      style={active ? { background: "#2A7FB8" } : undefined}
                    >
                      <span>{opt.emoji}</span> {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                Each day will include a transport tip — shop, app, route, fare in INR.
              </p>
            </div>

            <Field label="Anything else? (allergies, vibe, deal-breakers)">
              <textarea
                data-testid="builder-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Pure-veg only, no early starts, want one luxury hotel night..."
                className="input-base resize-none"
              />
            </Field>

            <button
              data-testid="btn-generate"
              onClick={generate}
              disabled={loading}
              className="w-full py-4 rounded-full font-semibold disabled:opacity-50 shadow-lg shadow-orange-200/70"
              style={{ background: "#FF7E4D", color: "#ffffff" }}
            >
              {loading
                ? "Crafting your trip-board…"
                : `Generate ${country?.name} itinerary`}
            </button>
          </div>

          {/* Preview side */}
          <aside className="lg:col-span-5 space-y-4">
            <div
              className="rounded-2xl overflow-hidden relative h-56 ring-1 ring-slate-200 shadow-sm"
              style={{ background: "#FFFFFF" }}
            >
              <img
                src={country?.image}
                alt={country?.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to top, rgba(11,37,69,0.78), transparent 65%)",
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#FFD7A8]">
                  — Your destination
                </span>
                <div className="font-serif text-3xl mt-1 text-white">
                  {country?.name}
                </div>
                <div className="text-xs text-white/85 mt-1">
                  {country?.tagline}
                </div>
              </div>
            </div>
            <div className="card-surface rounded-2xl p-5">
              <span className="kicker">— How it works</span>
              <ol className="mt-3 space-y-3 text-sm text-slate-700">
                <li>
                  <span style={{ color: "#2A7FB8" }} className="font-semibold">
                    01 ·
                  </span>{" "}
                  Pick country, dates, cities & interests.
                </li>
                <li>
                  <span style={{ color: "#2A7FB8" }} className="font-semibold">
                    02 ·
                  </span>{" "}
                  Our AI architect drafts a day-by-day trip-board.
                </li>
                <li>
                  <span style={{ color: "#2A7FB8" }} className="font-semibold">
                    03 ·
                  </span>{" "}
                  Review, jump to Explore for flights/stays, then download the
                  PDF.
                </li>
              </ol>
            </div>
          </aside>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div data-testid="itinerary-loading" className="mt-12">
            <LoadingTripBoard country={country?.name} />
          </div>
        )}

        {/* Itinerary trip board */}
        {itin && (
          <TripBoard itin={itin} slug={slug} />
        )}
      </div>
      <Footer />
      {itin?.id && (
        <ItineraryAssistant
          itineraryId={itin.id}
          country={itin.country_slug || slug}
          foodPreference={itin.food_preference}
          transportPreference={itin.transport_preference}
          onUpdate={(updated) => setItin(updated)}
        />
      )}
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <label className={`flex flex-col gap-1.5 ${className}`}>
    <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
      {label}
    </span>
    {children}
  </label>
);

const LOADING_STEPS = [
  "Reading your travel brief…",
  "Charting your route across cities…",
  "Picking veg-friendly food stops…",
  "Layering in transfers and timings…",
  "Estimating costs in ₹…",
  "Polishing the trip-board…",
];

function LoadingTripBoard({ country }) {
  const [step, setStep] = useState(0);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const id = setInterval(
      () => setStep((s) => (s + 1) % LOADING_STEPS.length),
      2400,
    );
    return () => clearInterval(id);
  }, []);
  return (
    <div className="card-surface rounded-2xl p-8 sm:p-12 flex flex-col items-center text-center gap-6">
      <div className="relative w-16 h-16">
        <div
          className="absolute inset-0 rounded-full animate-ping"
          style={{ background: "rgba(42,127,184,0.25)" }}
        />
        <div
          className="absolute inset-2 rounded-full grid place-items-center shadow-md"
          style={{ background: "#2A7FB8" }}
        >
          <Sparkle size={20} color="#ffffff" weight="fill" />
        </div>
      </div>
      <div>
        <div className="kicker">— Drafting your {country} trip-board</div>
        <p className="font-serif text-2xl sm:text-3xl mt-3 text-slate-900">
          {LOADING_STEPS[step]}
        </p>
        <p className="text-xs text-slate-500 mt-3 font-mono">
          Usually 15–25 seconds
        </p>
      </div>
      <div className="flex gap-1.5 mt-2">
        {LOADING_STEPS.map((_, i) => (
          <span
            key={i}
            className="h-1 w-8 rounded-full transition-colors"
            style={{
              background:
                i <= step ? "#2A7FB8" : "rgba(11,37,69,0.10)",
            }}
          />
        ))}
      </div>
    </div>
  );
}
